import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ErrorHandler,
  enableProdMode,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideMssFormly } from '@shared/components/formly/mss-formly';
import { AuthorizeService } from '@shared/services/authorize.service';
import { ConfigService } from '@shared/services/config.service';
import { MaintenanceService } from '@shared/services/maintenance.service';
import { LenderHubService } from '@shared/services/lenderhub.service';
import { SharedModule } from '@shared/shared.module';
import { OAuthModule } from 'angular-oauth2-oidc';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { MiGenericService } from './app/mi/mi-generic.service';
import { environment } from './environments/environment';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';

if (environment.production) {
  enableProdMode();
  Sentry.init({
    dsn: environment.sentry.dsn,
    attachStacktrace: true,
    debug: false,
    environment: 'production',
    maxValueLength: 5000,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.2,
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(BrowserModule, FormsModule, SharedModule.forRoot(environment.sharedConfig), ReactiveFormsModule),
    provideMssFormly(),
    LenderHubService,
    provideAppInitializer(() => {
      const configService = inject(ConfigService);
      const authorizeService = inject(AuthorizeService);
      const maintenanceService = inject(MaintenanceService);

      // If an API is in maintenance mode, still boot the app so the maintenance page can be shown
      return configService
        .load()
        .then(() => authorizeService.initialize())
        .catch((error: unknown) => maintenanceService.ignoreErrorIfInMaintenance(error));
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    MiGenericService,
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    ...(OAuthModule.forRoot().providers ?? []),
  ],
})
  // eslint-disable-next-line no-console
  .catch(err => console.error(err));
